import {useContext, useEffect, useState} from "react";
import {observer} from "mobx-react";

import Image from "../media/image/Image";
import Video from "../media/video/Video";

import classNames from "classnames";
import styles from "./styles/HeroContainer.module.scss";
import PropTypes from "prop-types";
import Markdown from "../utilities/Markdown";
import Logo from "./Logo";
import Button from "../utilities/Button";
import FlatIcon from "../utilities/FlatIcon";
import {StoreContext} from "../../stores/StoreLoader";
import {useHeroMediaItem} from "../../hooks/useHeroMediaItem";
import {isSchoolBlocksApp} from "../../utils/SchoolBlocksUtilities";
import FontAwesome from "../utilities/FontAwesome";

const headerTextContainerClassName = classNames({
    [styles.headerTextContainer]: true,
});
const editHeroButtonClassName = classNames({
    [styles.editHeroButton]: true,
});

const HeroContainer = observer((props) => {
    const {organizationStore, userStore, modalStore, styleStore} = useContext(StoreContext);

    const canEditDescription = userStore.isAdmin && !isSchoolBlocksApp();
    const [videoPaused, setVideoPaused] = useState(false);

    const heroMediaItem = useHeroMediaItem();

    function openEditHeroModal() {
        modalStore.addModal({
            type: 'edit-hero',
            id: 'edit-hero',
        })
    }

    function toggleEditing() {
        modalStore.addModal({
            type: 'edit-description',
            id: 'edit-description',
            handleUnmount: async () => {
                await organizationStore.fetchBlockItems({
                    blockType: 'about',
                    page: 1,
                    page_size: 12,
                    published: true
                });
                organizationStore.getContentAnalysis(); // get content analytics after toggling to be sure they are always correct
            }
        })
    }

    const headerTextClassName = classNames({
        [styles.headerText]: true,
        [styles.headerTextEditable]: canEditDescription,
    });
    const containerClassName = classNames({
        [styles.container]: true,
        [styles.containerIsWelcomePage]: props.isWelcomePage,
        [styles.containerFloatingBlocksTheme]: styleStore.themeID === "2",
    });

    useEffect(() => {
        const video = document.getElementById("sb-hero-video");
        function pausePlayVideo() {
            if (video.paused || video.ended) {
                video.play();
                setVideoPaused(false);
            } else {
                video.pause();
                setVideoPaused(true);
            }
        }
        const playPauseButton = document.getElementById("sb-hero-playpause");

        if (heroMediaItem?.type === "video") {
            playPauseButton.addEventListener("click", pausePlayVideo)
        }

        return () => {
            if (heroMediaItem?.type === "video") {
                playPauseButton.removeEventListener("click", pausePlayVideo);
            }
        }
    }, [heroMediaItem])

    let bgMedia = "";
    if (heroMediaItem?.type === 'image') {
        bgMedia = <Image backgroundType="cover" isBackground={true} filter="darkGradient" {...heroMediaItem}
                         imageUrl={heroMediaItem.media_url}/>;
    } else if (heroMediaItem?.type === 'video') {
        bgMedia =
            <Video backgroundType="cover" isBackground={true} {...heroMediaItem} id={"sb-hero-video"} mediaUrl={heroMediaItem.media_url}/>;
    }

    const descriptionItem = organizationStore.organization.json_data.settings?.headerText;
    const headerText = descriptionItem ? descriptionItem : (canEditDescription ? "Add Description" : null);

    return (
        <div className={containerClassName} id={'hero'}>
            {bgMedia}
            {heroMediaItem?.type === "video" && <button aria-label={videoPaused ? "Play Video" : "Pause Video"} id={"sb-hero-playpause"} className={styles.playPauseButton}>
                <FontAwesome ariaHidden={true} prefix={"fas"} name={videoPaused ? "fa-play" : "fa-pause"} />
            </button>}
            <div className={styles.heroText}>
                <Logo />
                {organizationStore.organization.json_data?.settings?.appearance?.hideHeaderTitle || <h2 className={styles.titleText}>
                    {organizationStore?.organization?.title}
                </h2>}
                <div className={headerTextContainerClassName}>
                    {headerText && <div className={headerTextClassName} onClick={() => canEditDescription && toggleEditing()}>
                        <div>
                            <Markdown
                                source={headerText}/>
                        </div>
                    </div>}
                    {props.displayViewHomepageButton === true && <div>
                        <button onClick={props.handleViewHomepage} className={styles.viewHomepageButton}>View Homepage</button>
                    </div>}
                </div>
            </div>
            {userStore.isAdmin && props.isWelcomePage &&
                <Button className={editHeroButtonClassName} onClick={openEditHeroModal}
                        aria-label={'Edit Hero Image'}>
                    <FlatIcon name={'flaticon-edit-1'}/>
                </Button>}
        </div>
    );
})

HeroContainer.propTypes = {
    displayViewHomepageButton: PropTypes.bool,
    handleViewHomepage: PropTypes.func,
};

HeroContainer.defaultProps = {
    displayViewHomepageButton: false,
};

export default HeroContainer;
